// FILE: src/main.jsx
import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";

// ✅ ADD THIS IMPORT
import { RestaurantProvider } from "./hooks/useRestaurantContext";

Sentry.init({
  dsn: "https://4c12a29bd2554d9a2781980980783661@o4511236584964096.ingest.us.sentry.io/4511236689887232",
  sendDefaultPii: true,
});

/**
 * ManiDine Debug Crash Overlay
 * - Shows runtime errors ON SCREEN when `?debug=1`
 * - Captures:
 *   - React render errors (ErrorBoundary)
 *   - window.onerror
 *   - unhandledrejection
 */

const isDebug =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("debug") === "1";

function safeString(x) {
  try {
    if (x == null) return "";
    if (typeof x === "string") return x;
    if (x instanceof Error) return x.stack || x.message || String(x);
    return JSON.stringify(x, null, 2);
  } catch {
    return String(x);
  }
}

function getStoredCrashes() {
  try {
    const raw = localStorage.getItem("__manidine_crash_log__");
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function pushCrash(entry) {
  try {
    const prev = getStoredCrashes();
    const next = [entry, ...prev].slice(0, 30);
    localStorage.setItem("__manidine_crash_log__", JSON.stringify(next));
  } catch {
    // ignore
  }
}

function DebugCrashPanel() {
  const [crashes, setCrashes] = React.useState(() =>
    isDebug ? getStoredCrashes() : []
  );

  React.useEffect(() => {
    if (!isDebug) return;

    const id = setInterval(() => {
      setCrashes(getStoredCrashes());
    }, 400);

    return () => clearInterval(id);
  }, []);

  if (!isDebug) return null;

  const latest = crashes?.[0];

  return (
    <div
      style={{
        position: "fixed",
        inset: "12px 12px auto 12px",
        zIndex: 999999,
        maxHeight: "46vh",
        overflow: "auto",
        background: "rgba(0,0,0,0.85)",
        color: "#fff",
        borderRadius: 12,
        padding: 12,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
        fontSize: 12,
        boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ fontWeight: 900 }}>MANIDINE DEBUG</div>
        <div style={{ opacity: 0.8 }}>
          crashes: {Array.isArray(crashes) ? crashes.length : 0}
        </div>

        <button
          type="button"
          onClick={() => {
            try {
              localStorage.removeItem("__manidine_crash_log__");
              setCrashes([]);
            } catch {}
          }}
          style={{
            marginLeft: "auto",
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "#fff",
            borderRadius: 10,
            padding: "6px 10px",
            cursor: "pointer",
            fontWeight: 800,
          }}
        >
          Clear log
        </button>
      </div>

      {!latest ? (
        <div style={{ marginTop: 10, opacity: 0.85 }}>
          No crash logged yet. Reproduce the crash now.
        </div>
      ) : (
        <>
          <div style={{ marginTop: 10, opacity: 0.85 }}>
            Latest: <strong>{latest.type}</strong> •{" "}
            {new Date(latest.time).toLocaleString()}
          </div>

          <pre
            style={{
              marginTop: 10,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.35,
              background: "rgba(255,255,255,0.08)",
              borderRadius: 10,
              padding: 10,
            }}
          >
            {latest.message}
          </pre>

          {latest.extra ? (
            <pre
              style={{
                marginTop: 10,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.35,
                background: "rgba(255,255,255,0.06)",
                borderRadius: 10,
                padding: 10,
                opacity: 0.9,
              }}
            >
              {latest.extra}
            </pre>
          ) : null}
        </>
      )}
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorText: "" };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, errorText: safeString(err) };
  }

  componentDidCatch(err, info) {
    const entry = {
      type: "react_error_boundary",
      time: Date.now(),
      message: safeString(err),
      extra: safeString(info?.componentStack || info),
      url: typeof window !== "undefined" ? window.location.href : "",
    };
    pushCrash(entry);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <>
        <DebugCrashPanel />
        <div style={{ padding: 24, fontFamily: "system-ui" }}>
          <h2 style={{ margin: 0 }}>ManiDine crashed</h2>
          <p style={{ opacity: 0.8, marginTop: 8 }}>
            (Debug: open with <code>?debug=1</code> to see details.)
          </p>
          <pre
            style={{
              marginTop: 12,
              padding: 12,
              background: "#111827",
              color: "#fff",
              borderRadius: 12,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {this.state.errorText}
          </pre>
        </div>
      </>
    );
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    const entry = {
      type: "window_error",
      time: Date.now(),
      message: safeString(event?.error || event?.message || event),
      extra: safeString({
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
      }),
      url: window.location.href,
    };
    pushCrash(entry);
  });

  window.addEventListener("unhandledrejection", (event) => {
    const entry = {
      type: "unhandled_rejection",
      time: Date.now(),
      message: safeString(event?.reason || event),
      url: window.location.href,
    };
    pushCrash(entry);
  });
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        {/* ✅ REQUIRED: Restaurant context for Dashboard/Menu/Orders/etc */}
        <RestaurantProvider>
          <App />
          <DebugCrashPanel />
        </RestaurantProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
